import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SectionShellEditor from "./SectionShellEditor";
import { getValueAtDescriptorPath } from "../elementEditing";

function GeneratedCatalogEditor({
  descriptor,
  form,
  disabled,
  onCommit,
  onToggleSection,
  onUploadImage,
  onRemoveImage,
}) {
  const [showPresentation, setShowPresentation] = React.useState(false);

  React.useEffect(() => {
    setShowPresentation(false);
  }, [descriptor?.sectionId]);

  return (
    <Stack spacing={1.5}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2.5,
          border: "1px solid rgba(25,36,47,.10)",
          bgcolor: "rgba(25,36,47,.025)",
        }}
      >
        <Stack spacing={0.8}>
          <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
              Se genera desde tu catálogo
            </Typography>
            <Chip size="small" label="Automático" />
          </Stack>
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.7)" }}>
            Los nombres, cantidades, subcategorías e imágenes de estas cards vienen de tus ítems publicados. No se duplican dentro del diseño del sitio.
          </Typography>
        </Stack>
      </Paper>

      <Button
        variant={showPresentation ? "text" : "contained"}
        disabled={disabled}
        onClick={() => setShowPresentation((current) => !current)}
        sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900 }}
      >
        {showPresentation ? "Ocultar ajustes visuales" : "Configurar presentación"}
      </Button>

      {showPresentation ? (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 950 }}>
              Presentación de categorías
            </Typography>
            <SectionShellEditor
              sectionId={descriptor.sectionId}
              form={form}
              disabled={disabled}
              onCommit={onCommit}
              onToggleSection={onToggleSection}
              onUploadImage={onUploadImage}
              onRemoveImage={onRemoveImage}
            />
          </Box>
        </>
      ) : null}
    </Stack>
  );
}

function ItemNamingEditor({ descriptor, form, disabled, onCommit }) {
  const singularField = descriptor?.singularField;
  const pluralField = descriptor?.pluralField;
  const singularValue = singularField
    ? getValueAtDescriptorPath(form, singularField.path, singularField.defaultValue ?? "")
    : "";
  const pluralValue = pluralField
    ? getValueAtDescriptorPath(form, pluralField.path, pluralField.defaultValue ?? "")
    : "";
  const singularPreview = String(singularValue || singularField?.defaultValue || "producto").trim() || "producto";
  const pluralPreview = String(pluralValue || pluralField?.defaultValue || "productos").trim() || "productos";

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.7)" }}>
        Definí cómo se nombra un ítem cuando mostramos cantidades en las categorías.
      </Typography>

      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
        <Chip label={`1 ${singularPreview}`} />
        <Chip label={`12 ${pluralPreview}`} variant="outlined" />
      </Stack>

      <TextField
        fullWidth
        size="small"
        label="Uno"
        value={singularValue ?? ""}
        disabled={disabled || !singularField}
        inputProps={{ maxLength: singularField?.maxLength }}
        onChange={(event) => singularField && onCommit(singularField.path, event.target.value)}
      />
      <TextField
        fullWidth
        size="small"
        label="Varios"
        value={pluralValue ?? ""}
        disabled={disabled || !pluralField}
        inputProps={{ maxLength: pluralField?.maxLength }}
        onChange={(event) => pluralField && onCommit(pluralField.path, event.target.value)}
      />

      <Paper elevation={0} sx={{ p: 1.25, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2 }}>
        <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.58)", fontWeight: 850 }}>
          Vista previa
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
          <Typography variant="body2" sx={{ fontWeight: 900 }}>1 {singularPreview}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 900 }}>12 {pluralPreview}</Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}

export default function CategoriesElementEditor(props) {
  if (props.descriptor?.kind === "catalog-derived") {
    return <GeneratedCatalogEditor {...props} />;
  }
  if (props.descriptor?.kind === "catalog-item-naming") {
    return <ItemNamingEditor {...props} />;
  }
  return null;
}
